import { Component, computed, inject, input } from '@angular/core';
import { AddinConfigService, ResourceStatus } from '@outlook-addin/shared';
import { translation } from '@cue/translations';
import { ResourceTimelineStateToColorPipe } from '../shared/pipes';
import { DatePipe } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ResourceUnavailabilityReason } from '@outlook-addin/cue-http';
import { formatDurationMinutesForDisplay } from '@assist/shared/data';

type PluralRule = 'zero' | 'one' | 'two' | 'few' | 'many' | 'other';

@Component({
  selector: 'addin-resource-availability',
  templateUrl: './resource-availability.component.html',
  styleUrls: ['./resource-availability.component.scss'],
  imports: [ResourceTimelineStateToColorPipe, DatePipe, TranslocoPipe],
})
export class ResourceAvailabilityComponent {
  protected readonly translation = translation;
  private readonly configService = inject(AddinConfigService);
  private readonly translocoService = inject(TranslocoService);
  private readonly dayUnitTranslationKeys: Record<PluralRule, string> = {
    zero: translation.outlookAddin.unavailabilityReasonDayZero,
    one: translation.outlookAddin.unavailabilityReasonDayOne,
    two: translation.outlookAddin.unavailabilityReasonDayTwo,
    few: translation.outlookAddin.unavailabilityReasonDayFew,
    many: translation.outlookAddin.unavailabilityReasonDayMany,
    other: translation.outlookAddin.unavailabilityReasonDayOther,
  };

  statusList: ResourceStatus[] = this.configService.value.configuration.statusesList;

  isRestrictedResource = input<boolean>();
  multipleResourceTypeReservationsNotAllowed = input(false);
  unavailabilityReason = input<ResourceUnavailabilityReason | null>(null);
  unavailabilityReasonValue = input<number | null>(null);
  startDate = input<Date>();
  endDate = input<Date>();

  timeline = input<string>();
  timelineWindowList = computed(() => {
    return this.timeline() ? this.timeline().split('') : [];
  });

  unavailabilityReasonTranslationKey = computed(() => {
    switch (this.unavailabilityReason()) {
      case ResourceUnavailabilityReason.MinEventDuration:
        return this.translation.outlookAddin.unavailabilityReasonMinEventDuration;
      case ResourceUnavailabilityReason.MaxEventDuration:
        return this.translation.outlookAddin.unavailabilityReasonMaxEventDuration;
      case ResourceUnavailabilityReason.MaxDaysAdvance:
        return this.translation.outlookAddin.unavailabilityReasonMaxDaysAdvance;
      case ResourceUnavailabilityReason.OutsideWorkingHours:
        return this.translation.outlookAddin.unavailabilityReasonOutsideWorkingHours;
      case ResourceUnavailabilityReason.Reserved:
        return this.translation.outlookAddin.unavailabilityReasonReserved;
      case ResourceUnavailabilityReason.EventEndsInPast:
        return this.translation.outlookAddin.unavailabilityReasonEventEndsInPast;
      default:
        return null;
    }
  });

  unavailabilityReasonTranslationParams = computed(() => {
    const value = this.unavailabilityReasonValue();

    switch (this.unavailabilityReason()) {
      case ResourceUnavailabilityReason.MinEventDuration:
      case ResourceUnavailabilityReason.MaxEventDuration:
        return {
          duration: value == null ? '' : this.formatDurationMinutes(value),
        };
      case ResourceUnavailabilityReason.MaxDaysAdvance:
        return {
          days: value ?? '',
          dayUnit: value == null ? '' : this.formatDayUnit(value),
        };
      default:
        return {};
    }
  });

  getStatusName(value: string) {
    return this.statusList?.filter((x) => x.status === +value)[0];
  }

  isSameDate() {
    if (this.startDate() && this.endDate()) {
      return (
        this.startDate().getDay() === this.endDate().getDay() &&
        this.startDate().getMonth() === this.endDate().getMonth() &&
        this.startDate().getFullYear() === this.endDate().getFullYear()
      );
    }
    return false;
  }

  private formatDurationMinutes(durationMinutes: number) {
    return formatDurationMinutesForDisplay(durationMinutes, (key) => this.translocoService.translate(key));
  }

  private formatDayUnit(dayCount: number) {
    const pluralRule = new Intl.PluralRules(this.translocoService.getActiveLang()).select(dayCount) as PluralRule;

    return this.translocoService.translate(this.dayUnitTranslationKeys[pluralRule]);
  }
}
